import React, { memo, useEffect, useRef, useState } from 'react';
import { Box, Button, Drop, Text, TextInput, CheckBox } from 'grommet';
import { AddCircle, Filter, FormDown, FormUp, FormClose } from 'grommet-icons';
import { getMapping } from '../../reporting-utils';
import * as itemTypes from '../../items/item-types';
import ItemTagSelector from '../item-tag-selector';
import { FTSelect, LabeledInput } from '../inputs';
import AddableEventSelect from '../addable-event-select';
import { getVerticals, getSubVerticals } from '../../utils';
import { groupBy, startCase } from 'lodash';
import EventTagSelector from '../filters/event-tag-selector';
import SourceidTagSelector from '../source-id-tag-selector';

const CAMPAIGN_ITEM_TYPES = [itemTypes.CAMPAIGN, itemTypes.PARTNER_CAMPAIGN];
const ADGROUP_ITEM_TYPES = [itemTypes.AD_GROUP];
const ADCREATIVE_ITEM_TYPES = [itemTypes.AD_CREATIVE];

export const filterCategories = {
  CAMPAIGN: 'campaign',
  ADS: 'ads',
  TRAFFIC: 'traffic',
  USER: 'user',
  //SURVEY: 'SURVEY',
  TAG: 'tag',
  QUERY_PARAM: 'query_param',
  BUSINESS_UNIT: 'business_unit'
};

const filterCategoryOrder = [
  'business_unit',
  'campaign',
  'ads',
  'query_param',
  'traffic',
  'user'
];

const excludeQueryParams = [
  'properties.url.queryParams.reward',
  'properties.url.queryParams.keyword',
  'properties.url.queryParams.subaff1',
  'properties.url.queryParams.sourceid',
  'properties.url.queryParams.subaff3',
  'properties.url.queryParams.subaff4',
  'properties.url.queryParams.subaff5'
];

export const FILTER_ITEMS = [
  {
    name: 'Traffic Partner',
    field: 'properties.trafficPartnerId',
    category: filterCategories.TRAFFIC,
    input: {
      type: 'itemTagSelector',
      itemType: itemTypes.TRAFFIC_PARTNER,
      canExclude: true
    }
  },
  {
    name: 'Subaff1',
    field: 'properties.url.queryParams.subaff1',
    category: filterCategories.TRAFFIC,
    input: {
      type: 'eventTagSelector'
    }
  },
  {
    name: 'Source ID (subaff2)',
    field: 'properties.url.queryParams.sourceid',
    category: filterCategories.TRAFFIC,
    input: {
      type: 'sourceidTagSelector',
      canExclude: true
    }
  },
  {
    name: 'Subaff3',
    field: 'properties.url.queryParams.subaff3',
    category: filterCategories.TRAFFIC,
    input: {
      type: 'eventTagSelector'
    }
  },
  {
    name: 'Subaff4',
    field: 'properties.url.queryParams.subaff4',
    category: filterCategories.TRAFFIC,
    input: {
      type: 'eventTagSelector'
    }
  },
  {
    name: 'Subaff5',
    field: 'properties.url.queryParams.subaff5',
    category: filterCategories.TRAFFIC,
    input: {
      type: 'eventTagSelector'
    }
  },
  {
    name: 'Advertiser',
    field: 'properties.advertiserId',
    category: filterCategories.CAMPAIGN,
    input: {
      type: 'itemTagSelector',
      itemType: itemTypes.ADVERTISER,
      canExclude: true
    }
  },
  {
    name: 'Campaign Type',
    field: 'target.itemType',
    category: filterCategories.CAMPAIGN,
    input: {
      type: 'filterSelect',
      options: [
        {
          key: 'offer',
          displayName: 'Performance'
        },
        {
          key: 'partnerCampaign',
          displayName: 'Partner'
        }
      ]
    }
  },
  {
    name: 'Campaign Name',
    field: 'properties.campaignId',
    category: filterCategories.CAMPAIGN,
    input: {
      type: 'itemTagSelector',
      itemTypes: CAMPAIGN_ITEM_TYPES,
      canExclude: true
    }
  },
  {
    name: 'Campaign Conversion Type',
    field: 'properties.conversionTypeName',
    category: filterCategories.CAMPAIGN,
    input: {
      type: 'eventTagSelector',
      canExclude: true
    }
  },
  {
    name: 'Campaign Vertical',
    field: 'properties.bannerData.campaignVerticalId',
    category: filterCategories.CAMPAIGN,
    input: {
      type: 'filterSelectSearch',
      source: () =>
        getVerticals().then(verticals =>
          verticals.map(v => ({
            key: v.id,
            displayName: v.name
          }))
        )
    }
  },
  {
    name: 'Campaign Subvertical',
    field: 'properties.bannerData.campaignSubVerticalId',
    category: filterCategories.CAMPAIGN,
    input: {
      type: 'filterSelectSearch',
      source: () =>
        getSubVerticals().then(subverticals =>
          subverticals.map(sv => ({
            key: sv.id,
            displayName: sv.name
          }))
        )
    }
  },
  {
    name: 'Source Vertical',
    field: 'properties.sourceVerticalId',
    category: filterCategories.CAMPAIGN,
    input: {
      type: 'filterSelectSearch',
      source: () =>
        getVerticals().then(verticals =>
          verticals.map(v => ({
            key: v.id,
            displayName: v.name
          }))
        )
    }
  },
  {
    name: 'Source Subvertical',
    field: 'properties.sourceSubVerticalId',
    category: filterCategories.CAMPAIGN,
    input: {
      type: 'filterSelectSearch',
      source: () =>
        getSubVerticals().then(subverticals =>
          subverticals.map(sv => ({
            key: sv.id,
            displayName: sv.name
          }))
        )
    }
  },
  {
    name: 'Ad Group',
    field: 'properties.adGroupId',
    category: filterCategories.CAMPAIGN,
    input: {
      type: 'itemTagSelector',
      itemTypes: ADGROUP_ITEM_TYPES,
      canExclude: true
    }
  },
  {
    name: 'Ad Creative',
    field: 'properties.adCreativeId',
    category: filterCategories.CAMPAIGN,
    input: {
      type: 'itemTagSelector',
      itemTypes: ADCREATIVE_ITEM_TYPES,
      canExclude: true
    }
  },
  {
    name: 'Sort Strategy',
    field: 'properties.sortStrategy',
    category: filterCategories.ADS,
    input: {
      type: 'filterSelect',
      options: [
        {
          key: 'epcSort',
          displayName: 'EPC Sort'
        },
        {
          key: 'MlControlledBidCreativeAndSorting',
          displayName: 'ML Controlled Creative, Bidding, And Sorting'
        },
        {
          key: 'predictiveRpiSort',
          displayName: 'Predictive RPI'
        },
        {
          key: 'positionPredictiveRpiSort',
          displayName: 'Predictive RPI By Position'
        },
        {
          key: 'random',
          displayName: 'Random'
        },
        {
          key: 'rpiSort',
          displayName: 'RPI Sort'
        }
      ]
    }
  },
  {
    name: 'Model Version',
    field: 'properties.sortVersion',
    category: filterCategories.ADS,
    input: {
      type: 'eventTagSelector'
    }
  },
  {
    name: 'Device Type',
    field: 'properties.device.osName',
    category: filterCategories.USER,
    input: {
      type: 'eventTagSelector'
    }
  },
  {
    name: 'Inferred Device Type',
    field: 'properties.device.inferredDeviceType',
    category: filterCategories.USER,
    input: {
      type: 'eventTagSelector'
    }
  },
  {
    name: 'Query Parameter',
    field: 'properties.url.queryParams',
    category: filterCategories.QUERY_PARAM,
    input: {
      type: 'eventTagCollection',
      excludeFields: excludeQueryParams
    }
  },
  //  {
  //    name: 'Tag',
  //    field: 'tag', //todo: this probs won't be on the event itself
  //    category: filterCategories.TAG
  //  },
  {
    name: 'Business Unit',
    field: 'properties.businessUnitId',
    category: filterCategories.BUSINESS_UNIT,
    input: {
      type: 'itemTagSelector',
      itemTypes: [itemTypes.BUSINESS_UNIT]
    }
  },
  {
    name: 'Scope',
    field: 'scope',
    category: filterCategories.BUSINESS_UNIT,
    input: {
      type: 'eventTagSelector'
    }
  },
  {
    name: 'Ip Resolved Country',
    field: 'properties.ipResolvedCountry',
    category: filterCategories.USER,
    input: {
      type: 'eventTagSelector'
    }
  },
  {
    name: 'Placement Experience',
    field: 'properties.url.queryParams.placementexperienceid',
    category: filterCategories.TRAFFIC,
    input: {
      type: 'itemTagSelector',
      itemTypes: [itemTypes.PLACEMENT_EXPERIENCE]
    }
  },
  {
    name: 'Placement Location',
    field: 'properties.url.queryParams.placementlocationid',
    category: filterCategories.TRAFFIC,
    input: {
      type: 'itemTagSelector',
      itemTypes: [itemTypes.PLACEMENT_LOCATION]
    }
  },
  {
    name: 'Position',
    field: 'properties.flowImpressionPosition',
    category: filterCategories.ADS,
    input: {
      type: 'filterSelect',
      options: [
        { displayName: 'Position 1', key: 1 },
        { displayName: 'Position 2', key: 2 },
        { displayName: 'Position 3', key: 3 },
        { displayName: 'Position 4', key: 4 },
        { displayName: 'Position 5', key: 5 },
        { displayName: 'Position 6', key: 6 }
      ]
    }
  },
  {
    name: 'Sub Partner Id',
    field: 'properties.url.queryParams.subpartnerid',
    category: filterCategories.TRAFFIC,
    input: {
      type: 'eventTagSelector',
      canExclude: true
    }
  },
  {
    name: 'Engagement Method',
    field: 'engagementMethod',
    category: filterCategories.USER,
    input: {
      type: 'filterSelect',
      canExclude: true,
      options: [
        { key: 'click', displayName: 'Click' },
        { key: 'scan', displayName: 'Scan' },
        { key: 'sms', displayName: 'SMS' },
        { key: 'email', displayName: 'Email' }
      ]
    }
  },
  {
    name: 'Partner Configuration',
    field: 'properties.url.queryParams.configid',
    category: filterCategories.TRAFFIC,
    input: {
      type: 'itemTagSelector',
      itemTypes: [
        itemTypes.EMBEDDED_FLOW_CONFIG,
        itemTypes.OFFER_SYNDICATION_CONFIG
      ],
      canExclude: true
    }
  }
];

export const APPLICABLE_FILTERS = [...FILTER_ITEMS];

export const EventTagCollection = ({
  flowIds,
  filter,
  filterFields, //properties.vars.xxx properties.vars.yyy
  onTagAdd,
  onTagRemove,
  onCollectionAdd,
  onCollectionRemove,
  onCollectionFieldChange,
  mapping,
  addError
}) => {
  const excludeFields = Array.isArray(filter.input.excludeFields)
    ? filter.input.excludeFields
    : [];

  //exclude specific fields from being rendered here because of overlap between free form query param fields and discrete reward and suabaff fields
  const groupedFields = groupBy(
    filterFields.filter(f => excludeFields.indexOf(f.field) === -1),
    'field'
  );
  return (
    <Box direction='column' gap='xsmall'>
      <Box
        direction='row'
        flex={{ shrink: 0 }}
        style={{ position: 'relative' }}
      >
        <Button
          style={{ position: 'absolute', top: '-26px', left: '120px' }}
          plain
          focusIndicator={false}
          disabled={!!filterFields.find(ff => typeof ff.key === 'undefined')}
          icon={<AddCircle style={{ transform: 'scale(0.7)' }} />}
          onClick={() => onCollectionAdd(filter.field)}
        />
      </Box>

      {Object.keys(groupedFields).map(gf => {
        const groupedFieldValues = groupedFields[gf]
          .filter(groupField => typeof groupField.key !== 'undefined')
          .map(groupField => groupField.key);

        return (
          <Box
            direction='row'
            flex={{ shrink: 0 }}
            key={gf}
            gap='small'
            align='center'
          >
            <Box flex={{ shrink: 0 }} alignSelf='start'>
              <AddableEventSelect
                fieldBase={filter.field}
                options={mapping.filter(m => m.field.startsWith(filter.field))}
                value={gf === filter.field ? null : gf}
                disabled={gf !== filter.field}
                onChange={newValue => {
                  onCollectionFieldChange(newValue);
                }}
              />
            </Box>

            <EventTagSelector
              flowIds={flowIds}
              size='small'
              disabled={gf === filter.field}
              field={gf}
              tags={groupedFieldValues}
              onTagAdd={tag => {
                onTagAdd(gf, tag);
              }}
              onTagRemove={tag => {
                onTagRemove(gf, tag);
              }}
              addError={addError}
            />
            <Button
              plain
              icon={<FormClose />}
              onClick={() => {
                onCollectionRemove(gf);
              }}
            />
          </Box>
        );
      })}
    </Box>
  );
};

/**
 * Handles toggling between include and exclude mode for a filter
 */
const handleToggleExcludeMode = ({
  isExcluding,
  filterField,
  filters,
  excludedFilters,
  setFilters,
  setExcludedFilters
}) => {
  const currentIncluded = filters.filter(f => f.field === filterField);
  const currentExcluded = excludedFilters.filter(f => f.field === filterField);

  if (isExcluding) {
    // Move from Include to Exclude
    setFilters(filters.filter(f => f.field !== filterField));
    setExcludedFilters([
      ...excludedFilters.filter(f => f.field !== filterField),
      ...currentIncluded
    ]);
  } else {
    // Move from Exclude to Include
    setExcludedFilters(excludedFilters.filter(f => f.field !== filterField));
    setFilters([
      ...filters.filter(f => f.field !== filterField),
      ...currentExcluded
    ]);
  }
};

/**
 * Handles adding an item to either included or excluded filters
 */
const handleFilterItemAdd = ({
  itemId,
  filterField,
  isExcludedFilter,
  filters,
  excludedFilters,
  setFilters,
  setExcludedFilters
}) => {
  if (isExcludedFilter) {
    // Remove from included if exists
    setFilters(
      filters.filter(f => !(f.field === filterField && f.key === itemId))
    );

    // Add to excluded if not already there
    if (
      !excludedFilters.some(f => f.field === filterField && f.key === itemId)
    ) {
      setExcludedFilters([
        ...excludedFilters,
        { field: filterField, key: itemId }
      ]);
    }
  } else {
    // Remove from excluded if exists
    setExcludedFilters(
      excludedFilters.filter(
        f => !(f.field === filterField && f.key === itemId)
      )
    );

    // Add to included if not already there
    if (!filters.some(f => f.field === filterField && f.key === itemId)) {
      setFilters([...filters, { field: filterField, key: itemId }]);
    }
  }
};

/**
 * Handles removing an item from both included and excluded filters
 */
const handleFilterItemRemove = ({
  itemId,
  filterField,
  filters,
  excludedFilters,
  setFilters,
  setExcludedFilters
}) => {
  const excludedIndex = excludedFilters.findIndex(
    f => f.field === filterField && f.key === itemId
  );
  const includedIndex = filters.findIndex(
    f => f.field === filterField && f.key === itemId
  );

  if (excludedIndex !== -1) {
    const newExcludedFilters = [...excludedFilters];
    newExcludedFilters.splice(excludedIndex, 1);
    setExcludedFilters(newExcludedFilters);
  }
  if (includedIndex !== -1) {
    const newFilters = [...filters];
    newFilters.splice(includedIndex, 1);
    setFilters(newFilters);
  }
};

/**
 * Reusable toggle component for include/exclude mode
 */
const ExcludeToggle = ({ isExcluded, onChange }) => (
  <Box direction='row' gap='xsmall'>
    <Text size='small'>Include</Text>
    <CheckBox toggle checked={isExcluded} onChange={onChange} />
    <Text size='small'>Exclude</Text>
  </Box>
);

export const FilterInput = memo(
  ({
    flowIds,
    filter,
    filters,
    setFilters,
    mapping,
    addError,
    excludedFilters,
    setExcludedFilters
  }) => {
    let input;
    const [options, setOptions] = useState(filter.input.options ?? []);
    const [filteredOptions, setFilteredOptions] = useState(
      filter.input.options ?? []
    );
    const [isExcludedFilter, setIsExcludedFilter] = useState(
      excludedFilters.some(f => f.field === filter?.field)
    );

    useEffect(() => {
      // If the filter has an async data source, source
      // call the function and set the returned value from the source
      // note that data parsing logic is to be done in filter.input.source
      if (filter.input.source) {
        filter.input.source().then(optionList => setOptions(optionList));
      }
    }, [filter.input]);

    // Keep isExcludedFilter in sync with parent excludedFilters changes
    useEffect(() => {
      setIsExcludedFilter(excludedFilters.some(f => f.field === filter?.field));
    }, [excludedFilters, filter?.field]);

    let filterFields = [];
    let filterValues = [];
    if (filter.input) {
      // Combine both included and excluded filters for display
      const includedFields = filters.filter(f =>
        f.field.startsWith(filter.field)
      );
      const excludedFields = excludedFilters.filter(f =>
        f.field.startsWith(filter.field)
      );
      filterFields = [...includedFields, ...excludedFields];
      filterValues = filterFields.map(f => f.key);

      switch (filter.input.type) {
        case 'itemTagSelector':
          input = (
            <Box direction='row-reverse' gap='small' align='center'>
              {filter.input.canExclude && (
                <ExcludeToggle
                  isExcluded={isExcludedFilter}
                  onChange={e => {
                    handleToggleExcludeMode({
                      isExcluding: e.target.checked,
                      filterField: filter.field,
                      filters,
                      excludedFilters,
                      setFilters,
                      setExcludedFilters
                    });
                    setIsExcludedFilter(e.target.checked);
                  }}
                />
              )}
              <ItemTagSelector
                size='small'
                itemType={filter.input.itemType}
                itemTypes={filter.input.itemTypes}
                itemIds={filterValues}
                onItemAdd={itemId => {
                  handleFilterItemAdd({
                    itemId,
                    filterField: filter.field,
                    isExcludedFilter,
                    filters,
                    excludedFilters,
                    setFilters,
                    setExcludedFilters
                  });
                }}
                onItemRemove={itemId => {
                  handleFilterItemRemove({
                    itemId,
                    filterField: filter.field,
                    filters,
                    excludedFilters,
                    setFilters,
                    setExcludedFilters
                  });
                }}
                width='auto'
              />
            </Box>
          );
          break;
        case 'eventTagSelector':
          input = (
            <Box direction='row-reverse' gap='small' align='center'>
              {filter.input.canExclude && (
                <ExcludeToggle
                  isExcluded={isExcludedFilter}
                  onChange={e => {
                    handleToggleExcludeMode({
                      isExcluding: e.target.checked,
                      filterField: filter.field,
                      filters,
                      excludedFilters,
                      setFilters,
                      setExcludedFilters
                    });
                    setIsExcludedFilter(e.target.checked);
                  }}
                />
              )}
              <EventTagSelector
                flowIds={flowIds}
                size='small'
                field={filter.field}
                tags={filterValues}
                onTagAdd={tag => {
                  handleFilterItemAdd({
                    itemId: tag,
                    filterField: filter.field,
                    isExcludedFilter,
                    filters,
                    excludedFilters,
                    setFilters,
                    setExcludedFilters
                  });
                }}
                onTagRemove={tag => {
                  handleFilterItemRemove({
                    itemId: tag,
                    filterField: filter.field,
                    filters,
                    excludedFilters,
                    setFilters,
                    setExcludedFilters
                  });
                }}
                addError={addError}
              />
            </Box>
          );
          break;
        case 'eventTagCollection':
          input = (
            <EventTagCollection
              flowIds={flowIds}
              filter={filter}
              filterFields={filterFields}
              onTagAdd={(field, tag) => {
                const newFilters = [...filters];
                const newFilter = newFilters.find(
                  nf => nf.field === field && typeof nf.key === 'undefined'
                );
                //editing a new filter that hasn't had a key set yet
                if (newFilter) {
                  newFilter.key = tag;
                } else {
                  newFilters.push({ field, key: tag });
                }

                setFilters(newFilters);
              }}
              onTagRemove={(field, tag) => {
                const newFilters = [...filters];
                const existingFilterIndex = newFilters.findIndex(
                  f => f.field === field && f.key === tag
                );
                if (existingFilterIndex !== -1) {
                  newFilters.splice(existingFilterIndex, 1);
                  setFilters(newFilters);
                }
              }}
              onCollectionFieldChange={field => {
                const newFilters = [...filters];
                const editedFilter = newFilters.find(
                  f => f.field === filter.field
                );
                if (editedFilter) {
                  editedFilter.field = field;
                  setFilters(newFilters);
                }
              }}
              onCollectionAdd={field => {
                const newFilters = [...filters];
                newFilters.push({ field });
                setFilters(newFilters);
              }}
              onCollectionRemove={field => {
                const newFilters = [...filters.filter(f => f.field !== field)];
                setFilters(newFilters);
              }}
              mapping={mapping}
              addError={addError}
            />
          );
          break;
        case 'filterSelect':
          input = (
            <Box direction='row-reverse' gap='small' align='center'>
              {filter.input.canExclude && (
                <ExcludeToggle
                  isExcluded={isExcludedFilter}
                  onChange={e => {
                    handleToggleExcludeMode({
                      isExcluding: e.target.checked,
                      filterField: filter.field,
                      filters,
                      excludedFilters,
                      setFilters,
                      setExcludedFilters
                    });
                    setIsExcludedFilter(e.target.checked);
                  }}
                />
              )}
              <FTSelect
                closeOnChange={false}
                options={options}
                size='small'
                value={options.filter(o => filterValues.indexOf(o.key) !== -1)}
                labelKey='displayName'
                valueKey='key'
                multiple
                messages={{
                  multiple: filter.input.multipleMessage ?? 'Multiple selected'
                }}
                onChange={eV => {
                  //build the newly selected fields for this filter
                  const newSelections = eV.map(v => ({
                    field: filter.field,
                    key: v.key
                  }));

                  if (isExcludedFilter) {
                    //replace all excluded fields for this filter
                    setExcludedFilters([
                      ...excludedFilters.filter(f => f.field !== filter.field),
                      ...newSelections
                    ]);
                  } else {
                    //replace all included fields for this filter
                    setFilters([
                      ...filters.filter(f => f.field !== filter.field),
                      ...newSelections
                    ]);
                  }
                }}
              />
            </Box>
          );
          break;
        case 'filterSelectSearch':
          input = (
            <FTSelect
              closeOnChange={false}
              options={filteredOptions.length > 0 ? filteredOptions : options}
              size='small'
              value={options.filter(o => filterValues.indexOf(o.key) !== -1)}
              labelKey='displayName'
              valueKey='key'
              multiple
              messages={{
                multiple: filter.input.multipleMessage ?? 'Multiple selected'
              }}
              onChange={eV => {
                //remove all fields for this filter
                const newFilters = [
                  ...filters.filter(f => f.field !== filter.field)
                ];

                //add in the newly selected fields
                newFilters.push(
                  ...eV.map(v => ({
                    field: filter.field,
                    key: v.key
                  }))
                );
                setFilters(newFilters);
              }}
              handleSearch={search => {
                const lowercaseSearch = search.toLowerCase();
                if (search === '') {
                  setFilteredOptions(options);
                } else {
                  setFilteredOptions(
                    options.filter(o => {
                      const lowercaseLabel = o.displayName.toLowerCase();
                      return lowercaseLabel.includes(lowercaseSearch);
                    })
                  );
                }
              }}
            />
          );
          break;
        case 'sourceidTagSelector':
          input = (
            <Box direction='row-reverse' gap='small' align='center'>
              {filter.input.canExclude && (
                <ExcludeToggle
                  isExcluded={isExcludedFilter}
                  onChange={e => {
                    handleToggleExcludeMode({
                      isExcluding: e.target.checked,
                      filterField: filter.field,
                      filters,
                      excludedFilters,
                      setFilters,
                      setExcludedFilters
                    });
                    setIsExcludedFilter(e.target.checked);
                  }}
                />
              )}
              <SourceidTagSelector
                size='small'
                itemIds={filterValues}
                onItemAdd={itemId => {
                  handleFilterItemAdd({
                    itemId,
                    filterField: filter.field,
                    isExcludedFilter,
                    filters,
                    excludedFilters,
                    setFilters,
                    setExcludedFilters
                  });
                }}
                onItemRemove={itemId => {
                  handleFilterItemRemove({
                    itemId,
                    filterField: filter.field,
                    filters,
                    excludedFilters,
                    setFilters,
                    setExcludedFilters
                  });
                }}
                width='auto'
                addError={addError}
              />
            </Box>
          );
          break;
        default:
        //noop
      }
    }
    return input;
  }
);

const FilterGroup = ({
  category,
  items,
  filters,
  setFilters,
  excludedFilters,
  setExcludedFilters,
  isExpanded,
  onIsExpandedChange,
  mapping,
  addError,
  flowIds
}) => {
  return (
    <Box direction='column' flex={{ shrink: 0 }} pad='small'>
      <Box
        direction='row'
        justify='between'
        align='center'
        flex={{ shrink: 0 }}
        focusIndicator={false}
        onClick={() => onIsExpandedChange(!isExpanded)}
      >
        <Text color='dark-4'>{startCase(category)}</Text>
        {isExpanded ? <FormUp /> : <FormDown />}
      </Box>
      {isExpanded && (
        <Box
          direction='column'
          flex={{ shrink: 0 }}
          margin={{ left: 'medium' }}
          gap='small'
          pad='small'
        >
          {items.map(fi => {
            return (
              <Box direction='row' key={fi.field} align='center' gap='small'>
                <LabeledInput label={fi.name} size='small'>
                  <FilterInput
                    filter={fi}
                    filters={filters}
                    setFilters={setFilters}
                    excludedFilters={excludedFilters}
                    setExcludedFilters={setExcludedFilters}
                    mapping={mapping}
                    addError={addError}
                    flowIds={flowIds}
                  />
                </LabeledInput>
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
};

const FilterModal = ({
  textRef,
  setIsOpen,
  sortedGroups,
  groupedItems,
  filters,
  setFilters,
  excludedFilters,
  setExcludedFilters,
  expandedGroups,
  setExpandedGroups,
  mapping,
  addError,
  flowIds
}) => {
  const [internalFilters, _setInternalFilters] = useState(filters);
  const [internalExcludedFilters, _setInternalExcludedFilters] = useState(
    excludedFilters
  );

  // Refs to avoid stale closure in onClickOutside - updated synchronously
  const internalFiltersRef = useRef(internalFilters);
  const internalExcludedFiltersRef = useRef(internalExcludedFilters);

  // Wrapper setters that update both state and ref synchronously
  const setInternalFilters = newFilters => {
    internalFiltersRef.current = newFilters;
    _setInternalFilters(newFilters);
  };

  const setInternalExcludedFilters = newExcludedFilters => {
    internalExcludedFiltersRef.current = newExcludedFilters;
    _setInternalExcludedFilters(newExcludedFilters);
  };

  return (
    <Drop
      target={textRef.current}
      align={{ top: 'bottom', left: 'left' }}
      elevation='medium'
      onClickOutside={() => {
        setFilters(
          internalFiltersRef.current.filter(f => typeof f.key !== 'undefined')
        );
        setExcludedFilters(
          internalExcludedFiltersRef.current.filter(
            f => typeof f.key !== 'undefined'
          )
        );
        setIsOpen(false);
      }}
    >
      <Box direction='column' pad='small' width='large' flex={{ shrink: 0 }}>
        {sortedGroups.length > 0 ? (
          sortedGroups.map(sg => {
            return (
              <FilterGroup
                key={sg}
                category={sg}
                items={groupedItems[sg]}
                filters={internalFilters}
                setFilters={setInternalFilters}
                excludedFilters={internalExcludedFilters}
                setExcludedFilters={setInternalExcludedFilters}
                isExpanded={expandedGroups.indexOf(sg) !== -1}
                onIsExpandedChange={isExpanded => {
                  if (isExpanded && expandedGroups.indexOf(sg) === -1) {
                    setExpandedGroups([...expandedGroups, sg]);
                  } else if (!isExpanded) {
                    const newExpandedGroups = [...expandedGroups];
                    newExpandedGroups.splice(newExpandedGroups.indexOf(sg), 1);
                    setExpandedGroups(newExpandedGroups);
                  }
                }}
                mapping={mapping}
                addError={addError}
                flowIds={flowIds}
              />
            );
          })
        ) : (
          <Box align='center' justify='center'>
            <Text>No matching results</Text>
          </Box>
        )}
      </Box>
    </Drop>
  );
};

export const getEventMapping = setEventMapping => {
  getMapping(ms => {
    setEventMapping(
      ms
        .filter(m => excludeQueryParams.indexOf(m) === -1)
        .map(m => {
          const mParts = m.split('.');
          return {
            field: m,
            displayName: mParts[mParts.length - 1]
          };
        })
    );
  }, true);
};

const FilterSelector = ({
  propFlowIds = [], // external flow ids to render
  filters,
  setFilters,
  excludedFilters,
  setExcludedFilters,
  addError
}) => {
  const [flowIds, setFlowIds] = useState([]);
  const [searchValue, setSearchValue] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState([]);
  const [mapping, setMapping] = useState();
  const textRef = useRef();

  const filteredFilterItems = APPLICABLE_FILTERS.filter(
    f => f.name.toLowerCase().indexOf(searchValue.toLowerCase()) !== -1
  );

  const groupedItems = groupBy(filteredFilterItems, 'category');
  const sortedGroups = filterCategoryOrder.filter(category =>
    Object.keys(groupedItems).includes(category)
  );
  const getFlowIds = () => {
    return filters.filter(f => f.field === 'properties.flowId').map(f => f.key);
  };

  useEffect(() => {
    getEventMapping(setMapping);
    setFlowIds(getFlowIds());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (propFlowIds.length > 0) {
      setFlowIds(propFlowIds);
    }
  }, [propFlowIds]);

  useEffect(() => {
    if (searchValue !== '') {
      setExpandedGroups([...sortedGroups]);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchValue]);

  return (
    <Box direction='row' gap='small' align='center'>
      <Button
        disabled={filters.length === 0 && excludedFilters.length === 0}
        margin={{ vertical: 'small' }}
        onClick={() => {
          setIsOpen(true);
          textRef.current.focus();
        }}
      >
        <Filter
          color={
            (filters.length > 0 || excludedFilters.length > 0) && 'brand500'
          }
        />
      </Button>

      <Box width='200px'>
        <TextInput
          placeholder='Add Filter'
          disabled={!mapping}
          ref={textRef}
          value={searchValue}
          onChange={e => {
            setSearchValue(e.target.value);
          }}
          onFocusCapture={() => setIsOpen(true)}
        />
        {isOpen && (
          <FilterModal
            expandedGroups={expandedGroups}
            setExpandedGroups={setExpandedGroups}
            textRef={textRef}
            setIsOpen={setIsOpen}
            sortedGroups={sortedGroups}
            groupedItems={groupedItems}
            filters={filters}
            setFilters={setFilters}
            excludedFilters={excludedFilters}
            setExcludedFilters={setExcludedFilters}
            mapping={mapping}
            addError={addError}
            flowIds={flowIds}
          />
        )}
      </Box>
    </Box>
  );
};

FilterSelector.displayName = 'FilterSelector';

export default FilterSelector;
